// V23.15.0 — Gouvernance & Conformité (GRC Hybride).
//
// Page unifiée qui regroupe les nouveaux objets GRC :
// Départements, Processus (arborescence), Taxonomie de risques, KRIs,
// Recommandations. Mode strictement additif : pas de réutilisation de
// composants critiques existants, pas de modification de Risques/Audits.
//
// L'i18n est résolue avec le pattern `t("saas.grc.*", { defaultValue })`
// pour rester fonctionnel même si les locales EN/DE ne sont pas encore
// peuplées.
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { notify } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GovIntakeTab } from "@/components/GovIntake";
import { GovDocumentsPanel } from "@/components/GovDocumentsPanel";
import { EvidenceGraphPanel } from "@/components/EvidenceGraphPanel";
import { ExportButton } from "@/components/ExportButton";
import { CampaignsPanel, IncidentsPanel } from "@/components/RecurrencePanels";
import { Link } from "react-router-dom";
import { Plus, Trash2, ShieldAlert, Workflow, Building2, Activity, ClipboardList, AlertTriangle, Grid3x3, Truck, Sparkles } from "lucide-react";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import GovAnalytics from "@/components/GovAnalytics";

function Section({ icon: Icon, title, subtitle, children, action }) {
  return (
    <div className="noam-card p-5 space-y-4" data-testid={`grc-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {Icon ? <Icon className="w-5 h-5 mt-0.5 text-zinc-500" /> : null}
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {subtitle ? <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        {action}
      </div>
      <div>{children}</div>
    </div>
  );
}

function useList(endpoint) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(Array.isArray(data) ? data : (data.items || []));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);
  useEffect(() => { reload(); }, [reload]);
  return { items, loading, reload };
}

// ============================================================================
// Departments
// ============================================================================
function DepartmentsTab() {
  const { items, reload } = useList("/workspace/grc/departments");
  const [form, setForm] = useState({ name: "", owner: "", description: "" });

  const create = async () => {
    if (!form.name.trim()) { notify.error("Nom requis"); return; }
    try {
      await api.post("/workspace/grc/departments", form);
      setForm({ name: "", owner: "", description: "" });
      notify.success("Département créé");
      reload();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce département ?")) return;
    try { await api.delete(`/workspace/grc/departments/${id}`); reload(); } catch { /* silent */ }
  };

  return (
    <Section icon={Building2} title="Départements" subtitle="Unités organisationnelles · lien optionnel avec le Positionnement"
      action={null}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <Input data-testid="grc-dept-name" placeholder="Nom du département" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input data-testid="grc-dept-owner" placeholder="Owner" value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
        <Button data-testid="grc-dept-create" onClick={create}><Plus className="w-4 h-4 mr-1" />Créer</Button>
      </div>
      <Textarea data-testid="grc-dept-desc" rows={2} placeholder="Description (optionnelle)" value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })} className="mb-3" />
      {items.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun département. Créez-en un ci-dessus.</p>
      ) : (
        <ul className="divide-y border rounded-md">
          {items.map((d) => (
            <li key={d.id} className="flex items-center justify-between p-3" data-testid={`grc-dept-row-${d.id}`}>
              <div>
                <p className="font-medium text-sm">{d.name}</p>
                <p className="text-xs text-zinc-500">{d.owner || "—"}{d.description ? ` · ${d.description}` : ""}</p>
              </div>
              <button onClick={() => del(d.id)} className="text-zinc-400 hover:text-red-500" data-testid={`grc-dept-del-${d.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// Processes (with sub-process hierarchy)
// ============================================================================
function ProcessesTab() {
  const [tree, setTree] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({ name: "", criticality: "medium", department_id: "", parent_process_id: "" });

  const loadAll = async () => {
    try {
      const [{ data: t }, { data: d }] = await Promise.all([
        api.get("/workspace/grc/processes/tree"),
        api.get("/workspace/grc/departments"),
      ]);
      setTree(t.items || []);
      setDepartments(d.items || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadAll(); }, []);

  const create = async () => {
    if (!form.name.trim()) { notify.error("Nom requis"); return; }
    try {
      const payload = { ...form };
      if (!payload.department_id) delete payload.department_id;
      if (!payload.parent_process_id) delete payload.parent_process_id;
      await api.post("/workspace/grc/processes", payload);
      setForm({ name: "", criticality: "medium", department_id: "", parent_process_id: "" });
      notify.success("Processus créé");
      loadAll();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce processus ?")) return;
    try { await api.delete(`/workspace/grc/processes/${id}`); loadAll(); } catch { /* silent */ }
  };

  const flatten = (nodes, depth = 0) => {
    const out = [];
    nodes.forEach((n) => {
      out.push({ ...n, _depth: depth });
      if (n.children?.length) out.push(...flatten(n.children, depth + 1));
    });
    return out;
  };
  const flat = flatten(tree);

  return (
    <Section icon={Workflow} title="Processus" subtitle="Hiérarchie avec sous-processus · lien optionnel Département">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <Input data-testid="grc-proc-name" placeholder="Nom du processus" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select data-testid="grc-proc-dept" className="noam-input" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
          <option value="">— Département (optionnel) —</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select data-testid="grc-proc-crit" className="noam-input" value={form.criticality} onChange={(e) => setForm({ ...form, criticality: e.target.value })}>
          <option value="low">Criticité : basse</option>
          <option value="medium">Criticité : moyenne</option>
          <option value="high">Criticité : haute</option>
          <option value="critical">Criticité : critique</option>
        </select>
        <Button data-testid="grc-proc-create" onClick={create}><Plus className="w-4 h-4 mr-1" />Créer</Button>
      </div>
      <select data-testid="grc-proc-parent" className="noam-input mb-3" value={form.parent_process_id}
        onChange={(e) => setForm({ ...form, parent_process_id: e.target.value })}>
        <option value="">— Pas de processus parent (racine) —</option>
        {flat.map((p) => <option key={p.id} value={p.id}>{"— ".repeat(p._depth)}{p.name}</option>)}
      </select>
      {flat.length === 0 ? (
        <p className="text-sm text-zinc-500">Aucun processus. Créez-en un ci-dessus.</p>
      ) : (
        <ul className="divide-y border rounded-md">
          {flat.map((p) => (
            <li key={p.id} className="flex items-center justify-between p-3" data-testid={`grc-proc-row-${p.id}`}
              style={{ paddingLeft: `${12 + p._depth * 18}px` }}>
              <div>
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-xs text-zinc-500">crit. {p.criticality}{p.department_id ? ` · dpt ${(departments.find((d) => d.id === p.department_id) || {}).name || ""}` : ""}</p>
              </div>
              <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-500" data-testid={`grc-proc-del-${p.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// Risk Taxonomy (Categories + Sub-Categories)
// ============================================================================
function RiskTaxonomyTab() {
  const { items: cats, reload: reloadCats } = useList("/workspace/grc/risk-categories");
  const { items: subs, reload: reloadSubs } = useList("/workspace/grc/risk-subcategories");
  const [catForm, setCatForm] = useState({ name: "", color: "#dc2626" });
  const [subForm, setSubForm] = useState({ name: "", category_id: "" });

  const createCat = async () => {
    if (!catForm.name.trim()) return notify.error("Nom requis");
    try { await api.post("/workspace/grc/risk-categories", catForm); setCatForm({ name: "", color: "#dc2626" }); notify.success("Catégorie créée"); reloadCats(); }
    catch (e) { notify.error(e?.response?.data?.detail || "Erreur"); }
  };
  const createSub = async () => {
    if (!subForm.name.trim() || !subForm.category_id) return notify.error("Nom et catégorie requis");
    try { await api.post("/workspace/grc/risk-subcategories", subForm); setSubForm({ name: "", category_id: subForm.category_id }); notify.success("Sous-catégorie créée"); reloadSubs(); }
    catch (e) { notify.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <Section icon={ShieldAlert} title="Taxonomie de risques" subtitle="Catégorie → Sous-catégorie · alimente les Risques">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold mb-2">Catégories</h3>
          <div className="flex gap-2 mb-2">
            <Input data-testid="grc-cat-name" placeholder="Nom" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} />
            <input type="color" data-testid="grc-cat-color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="w-10 h-9 rounded border" />
            <Button data-testid="grc-cat-create" onClick={createCat}>Ajouter</Button>
          </div>
          {cats.length === 0 ? <p className="text-xs text-zinc-500">Aucune catégorie.</p> : (
            <ul className="space-y-1">
              {cats.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-sm" data-testid={`grc-cat-row-${c.id}`}>
                  <span className="inline-block w-3 h-3 rounded" style={{ background: c.color || "#dc2626" }} />
                  {c.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2">Sous-catégories</h3>
          <div className="flex gap-2 mb-2">
            <Input data-testid="grc-sub-name" placeholder="Nom" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} />
            <select data-testid="grc-sub-cat" className="noam-input" value={subForm.category_id} onChange={(e) => setSubForm({ ...subForm, category_id: e.target.value })}>
              <option value="">— Catégorie —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button data-testid="grc-sub-create" onClick={createSub}>Ajouter</Button>
          </div>
          {subs.length === 0 ? <p className="text-xs text-zinc-500">Aucune sous-catégorie.</p> : (
            <ul className="space-y-1">
              {subs.map((s) => (
                <li key={s.id} className="text-sm" data-testid={`grc-sub-row-${s.id}`}>
                  {s.name} <span className="text-xs text-zinc-500">· {(cats.find((c) => c.id === s.category_id) || {}).name || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Section>
  );
}

// ============================================================================
// KRIs
// ============================================================================
function KRIsTab() {
  const { items: kris, reload } = useList("/workspace/grc/kris");
  const { items: risks } = useList("/workspace/risks");
  const [form, setForm] = useState({ name: "", risk_id: "", threshold: 0, current_value: 0, unit: "%", direction: "up" });

  const create = async () => {
    if (!form.name.trim() || !form.risk_id) return notify.error("Nom et risque requis");
    try { await api.post("/workspace/grc/kris", { ...form, threshold: Number(form.threshold), current_value: Number(form.current_value) }); notify.success("KRI créé"); reload(); }
    catch (e) { notify.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce KRI ?")) return; try { await api.delete(`/workspace/grc/kris/${id}`); reload(); } catch { /* silent */ } };
  const inBreach = (k) => {
    const v = Number(k.current_value || 0), t = Number(k.threshold || 0);
    return k.direction === "down" ? v < t : v > t;
  };

  return (
    <Section icon={Activity} title="KRIs (Key Risk Indicators)" subtitle="Seuil + valeur courante · alerte si dépassement">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3">
        <Input data-testid="grc-kri-name" placeholder="Nom KRI" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="md:col-span-2" />
        <select data-testid="grc-kri-risk" className="noam-input" value={form.risk_id} onChange={(e) => setForm({ ...form, risk_id: e.target.value })}>
          <option value="">— Risque parent —</option>
          {risks.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
        <Input data-testid="grc-kri-threshold" type="number" placeholder="Seuil" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
        <Input data-testid="grc-kri-value" type="number" placeholder="Valeur" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} />
        <Button data-testid="grc-kri-create" onClick={create}>Ajouter</Button>
      </div>
      {kris.length === 0 ? <p className="text-sm text-zinc-500">Aucun KRI.</p> : (
        <ul className="divide-y border rounded-md">
          {kris.map((k) => (
            <li key={k.id} className="flex items-center justify-between p-3" data-testid={`grc-kri-row-${k.id}`}>
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  {k.name}
                  {inBreach(k) && <span className="text-[10px] uppercase font-semibold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">breach</span>}
                </p>
                <p className="text-xs text-zinc-500">{k.current_value}{k.unit || ""} / seuil {k.threshold}{k.unit || ""} · {k.direction === "down" ? "alerte si <" : "alerte si >"}</p>
              </div>
              <button onClick={() => del(k.id)} className="text-zinc-400 hover:text-red-500" data-testid={`grc-kri-del-${k.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// Recommendations
// ============================================================================
function RecommendationsTab() {
  const { items: recos, reload } = useList("/workspace/grc/recommendations");
  const { items: risks } = useList("/workspace/risks");
  const [form, setForm] = useState({ title: "", risk_id: "", severity: "moyenne", status: "ouverte", owner: "" });

  const create = async () => {
    if (!form.title.trim() || !form.risk_id) return notify.error("Titre et risque requis");
    try { await api.post("/workspace/grc/recommendations", form); notify.success("Recommandation créée"); reload(); }
    catch (e) { notify.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cette recommandation ?")) return; try { await api.delete(`/workspace/grc/recommendations/${id}`); reload(); } catch { /* silent */ } };

  return (
    <Section icon={ClipboardList} title="Recommandations" subtitle="Rattachées à un Audit, un Risque ou un Process">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
        <Input data-testid="grc-reco-title" className="md:col-span-2" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <select data-testid="grc-reco-risk" className="noam-input" value={form.risk_id} onChange={(e) => setForm({ ...form, risk_id: e.target.value })}>
          <option value="">— Risque —</option>
          {risks.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
        <select data-testid="grc-reco-sev" className="noam-input" value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
          <option value="critique">Critique</option>
          <option value="majeure">Majeure</option>
          <option value="moyenne">Moyenne</option>
          <option value="mineure">Mineure</option>
        </select>
        <Button data-testid="grc-reco-create" onClick={create}>Ajouter</Button>
      </div>
      {recos.length === 0 ? <p className="text-sm text-zinc-500">Aucune recommandation.</p> : (
        <ul className="divide-y border rounded-md">
          {recos.map((r) => (
            <li key={r.id} className="flex items-center justify-between p-3" data-testid={`grc-reco-row-${r.id}`}>
              <div>
                <p className="font-medium text-sm">{r.title}</p>
                <p className="text-xs text-zinc-500">{r.severity} · {r.status} {r.owner ? `· ${r.owner}` : ""}</p>
              </div>
              <button onClick={() => del(r.id)} className="text-zinc-400 hover:text-red-500" data-testid={`grc-reco-del-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// Phantoms GRC (deterministic findings)
// ============================================================================
function PhantomsTab() {
  const [data, setData] = useState({ findings: [], by_type: {} });
  const reload = async () => { try { const { data } = await api.get("/workspace/grc/phantoms"); setData(data); } catch { /* silent */ } };
  useEffect(() => { reload(); }, []);

  return (
    <Section icon={AlertTriangle} title="Fantômes GRC" subtitle="Patterns déterministes : Risk sans Control, KRI en breach sans plan, Audit critique sans reco">
      <Button size="sm" variant="outline" onClick={reload} data-testid="grc-phantoms-refresh" className="mb-3">Rafraîchir</Button>
      {data.findings.length === 0 ? (
        <p className="text-sm text-emerald-600">Aucun fantôme détecté — couverture nominale.</p>
      ) : (
        <ul className="divide-y border rounded-md">
          {data.findings.map((f, i) => (
            <li key={`${f.type}-${f.subject_id}-${i}`} className="p-3" data-testid={`grc-phantom-${i}`}>
              <p className="font-medium text-sm">
                <span className={`inline-block text-[10px] uppercase font-semibold mr-2 px-1.5 py-0.5 rounded ${
                  f.severity === "critique" ? "bg-red-500/10 text-red-400" :
                  f.severity === "majeure" ? "bg-orange-500/10 text-orange-400" :
                  f.severity === "moyenne" ? "bg-amber-500/10 text-amber-400" :
                  "bg-white/10 text-zinc-300"
                }`}>{f.severity}</span>
                {f.subject_label}
              </p>
              <p className="text-xs text-zinc-500 mt-1">{f.type} → attendu : {f.expected}</p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// V23.15.2 — Coverage Matrix heatmap (Process × Risk × couverture)
// + NOAM auto-suggest pour risques orphelins
// ============================================================================
function HeatmapTab() {
  const [data, setData] = useState({ matrix: [], risks_unassigned: [], risks_unassigned_count: 0, process_count: 0, risks_assigned_count: 0 });
  const [loading, setLoading] = useState(false);
  const [suggestModal, setSuggestModal] = useState(null); // {risk_id, risk_title}
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/workspace/grc/coverage-matrix");
      setData(d);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const askClaude = async (risk_id, risk_title) => {
    setSuggestModal({ risk_id, risk_title });
    setSuggestions(null);
    setSuggesting(true);
    try {
      const { data: r } = await api.post("/workspace/grc/suggest-controls", { risk_id, max_suggestions: 3 });
      setSuggestions(r);
    } catch (e) {
      notify.error(e?.response?.data?.detail || "NOAM indisponible");
      setSuggestions({ suggestions: [], error: true });
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = async (control, risk_id) => {
    try {
      await api.post("/workspace/controles", {
        nom: control.nom,
        type: control.type,
        risque_id: risk_id,
        frequence: control.frequence || "mensuel",
        efficacite: control.efficacite_estimee || 70,
        statut: "actif",
        commentaire: control.rationale || "",
      });
      notify.success(`Contrôle « ${control.nom} » créé`);
      reload(); // refresh la matrice
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Erreur création contrôle");
    }
  };

  const cellColor = (row) => {
    if (row.risk_count === 0) return "bg-white/[0.03] border-white/10 text-zinc-500";
    const ratio = row.uncovered_count / row.risk_count;
    if (ratio === 0) return "bg-emerald-50 border-emerald-300 text-emerald-700";
    if (ratio < 0.34) return "bg-amber-500/10 border-amber-500/40 text-amber-400";
    if (ratio < 0.67) return "bg-orange-500/10 border-orange-500/40 text-orange-400";
    return "bg-red-500/10 border-red-500/40 text-red-400";
  };

  return (
    <Section icon={Grid3x3} title="Heatmap Process × Risques"
      subtitle={`${data.process_count} processus · ${data.risks_assigned_count} risques assignés · ${data.risks_unassigned_count} orphelins${data.total_cost_of_inaction_chf ? ` · Coût inaction estimé ~${(data.total_cost_of_inaction_chf / 1000).toFixed(0)} k CHF/an` : ""}`}
      action={<Button size="sm" variant="outline" onClick={reload} disabled={loading} data-testid="grc-heatmap-refresh">Rafraîchir</Button>}>
      {loading && <p className="text-sm text-zinc-500">Calcul…</p>}
      {!loading && data.matrix.length === 0 && (
        <p className="text-sm text-zinc-500">Aucun processus créé. Crée d&apos;abord un processus dans l&apos;onglet Processus.</p>
      )}
      {/* V23.15.32 — Doctrine 3Q : bannière "top urgent" */}
      {data.top_urgent_orphan && data.top_urgent_orphan.cost_of_inaction_chf > 0 && (
        <div
          className="mb-4 p-3 rounded-md border-2 border-red-500/50 bg-red-500/10 flex items-start gap-3 flex-wrap"
          data-testid="grc-top-urgent-banner"
        >
          <Sparkles className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-red-700">
              Risque orphelin le plus urgent
            </div>
            <div className="text-sm font-semibold text-red-900 mt-0.5">
              {data.top_urgent_orphan.title}
            </div>
            <div className="text-xs text-red-700 mt-1">
              Coût d&apos;inaction estimé : <span className="font-bold">~{(data.top_urgent_orphan.cost_of_inaction_chf / 1000).toFixed(0)} k CHF/an</span>
              {" · "}Sévérité {data.top_urgent_orphan.severity}/25
            </div>
          </div>
          <button
            onClick={() => askClaude(data.top_urgent_orphan.id, data.top_urgent_orphan.title)}
            className="shrink-0 text-xs uppercase font-bold bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-700 inline-flex items-center gap-1.5 shadow-md ring-2 ring-red-300"
            data-testid="grc-top-urgent-suggest"
          >
            <Sparkles className="w-3.5 h-3.5" /> Suggérer NOAM en priorité
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.matrix.map((row, idx) => (
          <div
            key={row.process.id}
            className={`p-3 border-2 rounded-md ${cellColor(row)} ${idx === 0 && row.cost_of_inaction_chf > 0 ? "ring-2 ring-offset-1 ring-red-400" : ""}`}
            data-testid={`grc-heatmap-cell-${row.process.id}`}
          >
            <p className="font-medium text-sm">{row.process.name}</p>
            <p className="text-xs opacity-80 mt-1">
              crit. {row.process.criticality} · {row.risk_count} risques
              {row.cost_of_inaction_chf > 0 && (
                <> · <span className="font-semibold">~{(row.cost_of_inaction_chf / 1000).toFixed(0)} k CHF/an</span> inaction</>
              )}
            </p>
            <div className="mt-2 flex items-center gap-3 text-[11px] font-semibold uppercase">
              <span>✓ {row.covered_count} couverts</span>
              <span>✗ {row.uncovered_count} orphelins</span>
            </div>
            {row.risks.filter((r) => !r.covered).slice(0, 3).map((r) => (
              <div key={r.id} className="mt-2 text-xs flex items-center justify-between gap-2">
                <span className="truncate" title={r.cost_of_inaction_chf ? `Coût inaction : ~${(r.cost_of_inaction_chf / 1000).toFixed(0)} k CHF/an` : ""}>{r.title}</span>
                <button onClick={() => askClaude(r.id, r.title)}
                  className="shrink-0 text-[10px] uppercase font-semibold bg-white/10 px-1.5 py-0.5 rounded border border-current hover:bg-white/20 inline-flex items-center gap-1"
                  data-testid={`grc-suggest-${r.id}`}>
                  <Sparkles className="w-3 h-3" /> Suggérer
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      {data.risks_unassigned.length > 0 && (
        <div className="mt-6 p-4 border border-white/10 rounded-md bg-white/[0.04]" data-testid="grc-orphan-risks">
          <h3 className="text-sm font-semibold mb-2">Risques sans processus rattaché ({data.risks_unassigned.length})</h3>
          <p className="text-xs text-zinc-500 mb-3">Rattache-les à un processus dans Risques pour qu&apos;ils apparaissent dans la heatmap.</p>
          <ul className="space-y-1">
            {data.risks_unassigned.map((r) => (
              <li key={r.id} className="flex items-center justify-between text-xs" data-testid={`grc-orphan-${r.id}`}>
                <span>{r.title} {r.covered ? <span className="text-emerald-600">· couvert</span> : <span className="text-red-600">· orphelin</span>}</span>
                {!r.covered && (
                  <button onClick={() => askClaude(r.id, r.title)}
                    className="text-[10px] uppercase font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Suggérer NOAM
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Modal de suggestions NOAM */}
      {suggestModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSuggestModal(null)} data-testid="grc-suggest-modal">
          <div className="bg-[#0E1420] border border-white/10 text-zinc-200 max-w-2xl w-full rounded-lg shadow-xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-emerald-600 font-semibold">Suggestions NOAM · Doctrine + Secteur</p>
                <h2 className="text-base font-semibold">{suggestModal.risk_title}</h2>
              </div>
              <button onClick={() => setSuggestModal(null)} className="text-zinc-400 hover:text-zinc-200" data-testid="grc-suggest-close">×</button>
            </div>
            {suggesting && <p className="text-sm text-zinc-500">NOAM réfléchit…</p>}
            {!suggesting && suggestions?.skipped && (
              <p className="text-sm text-amber-400 bg-amber-500/10 p-3 rounded">⚠️ {suggestions.skip_reason}</p>
            )}
            {!suggesting && !suggestions?.skipped && (
              <>
                {suggestions?.doctrine_used && (
                  <p className="text-[11px] text-emerald-600 mb-3">✓ Doctrine active prise en compte</p>
                )}
                {(suggestions?.suggestions || []).flatMap((s) => s.controls.map((c, i) => ({ ...c, _risk_id: s.risk_id, _idx: i }))).length === 0 && (
                  <p className="text-sm text-zinc-500">NOAM n&apos;a pas produit de suggestion exploitable. {suggestions?.raw_preview ? `Aperçu : ${suggestions.raw_preview}…` : ""}</p>
                )}
                <ul className="space-y-3">
                  {(suggestions?.suggestions || []).flatMap((s) => s.controls.map((c, i) => ({ ...c, _risk_id: s.risk_id, _idx: i }))).map((c) => (
                    <li key={`${c._risk_id}-${c._idx}`} className="border rounded-md p-3" data-testid={`grc-suggest-card-${c._idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{c.nom}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            <span className={`inline-block text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded mr-2 ${
                              c.type === "preventif" ? "bg-blue-500/10 text-blue-400" :
                              c.type === "detectif" ? "bg-purple-50 text-purple-700" :
                              "bg-amber-500/10 text-amber-400"
                            }`}>{c.type}</span>
                            {c.frequence} · efficacité estimée {c.efficacite_estimee}%
                          </p>
                          <p className="text-xs text-zinc-600 mt-2">{c.rationale}</p>
                        </div>
                        <Button size="sm" onClick={() => { acceptSuggestion(c, c._risk_id); }} data-testid={`grc-accept-${c._idx}`}>
                          <Plus className="w-3 h-3 mr-1" /> Créer
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

// ============================================================================
// V23.15.2 — Suppliers GRC (fournisseurs avec FKs annuaire/process/department)
// ============================================================================
function SuppliersTab() {
  const { items: suppliers, reload } = useList("/workspace/fournisseurs");
  const { items: processes } = useList("/workspace/grc/processes");
  const { items: departments } = useList("/workspace/grc/departments");
  const { items: risks } = useList("/workspace/risks");
  const [form, setForm] = useState({
    nom: "", categorie: "Technologique", criticite: "moyenne",
    contact: "", contrat_eur: 0, score_risque: 50,
    annuaire_entity_id: "", process_id: "", department_id: "", risque_ids: [],
  });

  const create = async () => {
    if (!form.nom.trim()) return notify.error("Nom requis");
    try {
      const payload = { ...form, contrat_eur: Number(form.contrat_eur), score_risque: Number(form.score_risque) };
      // Nettoyer les FKs vides → null
      ["annuaire_entity_id", "process_id", "department_id"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.post("/workspace/fournisseurs", payload);
      setForm({ ...form, nom: "", contact: "" });
      notify.success("Fournisseur créé");
      reload();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce fournisseur ?")) return;
    try { await api.delete(`/workspace/fournisseurs/${id}`); reload(); } catch { /* silent */ }
  };
  const toggleRisk = (rid) => {
    setForm({
      ...form,
      risque_ids: form.risque_ids.includes(rid)
        ? form.risque_ids.filter((x) => x !== rid)
        : [...form.risque_ids, rid],
    });
  };

  return (
    <Section icon={Truck} title="Fournisseurs (GRC)" subtitle="FK Annuaire · FK Process · FK Department · risques exposés">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <Input data-testid="grc-sup-name" placeholder="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
        <Input data-testid="grc-sup-contact" placeholder="Contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
        <select data-testid="grc-sup-crit" className="noam-input" value={form.criticite} onChange={(e) => setForm({ ...form, criticite: e.target.value })}>
          <option value="critique">Criticité : critique</option>
          <option value="haute">Criticité : haute</option>
          <option value="moyenne">Criticité : moyenne</option>
          <option value="faible">Criticité : faible</option>
        </select>
        <Button data-testid="grc-sup-create" onClick={create}><Plus className="w-4 h-4 mr-1" />Créer</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <select data-testid="grc-sup-process" className="noam-input" value={form.process_id} onChange={(e) => setForm({ ...form, process_id: e.target.value })}>
          <option value="">— Process (optionnel) —</option>
          {processes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="grc-sup-dept" className="noam-input" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
          <option value="">— Département (optionnel) —</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <Input data-testid="grc-sup-contrat" type="number" placeholder="Contrat EUR" value={form.contrat_eur} onChange={(e) => setForm({ ...form, contrat_eur: e.target.value })} />
      </div>
      {risks.length > 0 && (
        <div className="mb-3 p-2 border rounded-md">
          <p className="text-xs text-zinc-500 mb-1">Risques exposés à ce fournisseur :</p>
          <div className="flex flex-wrap gap-1">
            {risks.slice(0, 12).map((r) => (
              <button key={r.id} onClick={() => toggleRisk(r.id)}
                className={`text-[11px] px-1.5 py-0.5 rounded border ${
                  form.risque_ids.includes(r.id) ? "bg-red-500/10 border-red-500/40 text-red-400" : "border-white/15 text-zinc-400 hover:bg-white/10"
                }`}
                data-testid={`grc-sup-risk-${r.id}`}>
                {r.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {suppliers.length === 0 ? <p className="text-sm text-zinc-500">Aucun fournisseur.</p> : (
        <ul className="divide-y border rounded-md">
          {suppliers.map((s) => (
            <li key={s.id} className="flex items-center justify-between p-3" data-testid={`grc-sup-row-${s.id}`}>
              <div>
                <p className="font-medium text-sm flex items-center gap-2">
                  {s.nom}
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                    s.criticite === "critique" ? "bg-red-500/10 text-red-400" :
                    s.criticite === "haute" ? "bg-orange-500/10 text-orange-400" :
                    "bg-white/10 text-zinc-300"
                  }`}>{s.criticite}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {s.categorie} · contrat {s.contrat_eur?.toLocaleString?.() || 0} EUR
                  {s.process_id ? ` · ${(processes.find((p) => p.id === s.process_id) || {}).name}` : ""}
                  {(s.risque_ids?.length || 0) > 0 ? ` · ${s.risque_ids.length} risque(s)` : ""}
                </p>
              </div>
              <button onClick={() => del(s.id)} className="text-zinc-400 hover:text-red-500" data-testid={`grc-sup-del-${s.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function Gouvernance() {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const loadSummary = async () => { try { const { data } = await api.get("/workspace/grc/summary"); setSummary(data); } catch { /* silent */ } };
  useEffect(() => { loadSummary(); }, []);
  const onMaterialized = () => { loadSummary(); setRefreshKey((k) => k + 1); };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="gouvernance-page">
      <ThreeQPageHeader key={refreshKey} endpoint="/workspace/gouvernance/top-priority" />
      <header className="space-y-1">
        <div className="noam-overline">{t("saas.grc.overline", { defaultValue: "GRC Hybride · V23.15.0" })}</div>
        <h1 className="noam-display text-3xl font-bold tracking-tight">
          {t("saas.grc.title", { defaultValue: "Gouvernance & Conformité" })}
        </h1>
        <p className="text-sm text-zinc-500">
          {t("saas.grc.subtitle", { defaultValue: "Départements · Processus · Taxonomie de risques · KRIs · Recommandations. Hybridés nativement avec l'Annuaire, l'Ontologie, le Phantom Detector et la Doctrine." })}
        </p>
        <div className="pt-1 flex gap-2 flex-wrap">
          <ExportButton system="governance" label="Exporter Governance (intégral)" />
          <Link to="/app/board-pack" className="btn-ghost text-[10px] border border-[#378ADD]/40 text-[#378ADD]" data-testid="gouvernance-board-pack-link">Dossier Comité</Link>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="grc-summary">
          {[
            ["Départements", summary.departments],
            ["Processus", summary.processes],
            ["Catégories", summary.risk_categories],
            ["KRIs", summary.kris],
            ["Recos", summary.recommendations],
            ["Risques non couverts", summary.uncovered_risks_count],
          ].map(([label, val]) => (
            <div key={label} className="noam-card p-3 text-center" data-testid={`grc-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="text-2xl font-semibold">{val ?? 0}</p>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-1">{label}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="intake" className="space-y-4">
        <TabsList data-testid="grc-tabs">
          <TabsTrigger value="analytics" data-testid="grc-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="intake" data-testid="grc-tab-intake">Intake</TabsTrigger>
          <TabsTrigger value="sources" data-testid="grc-tab-sources">Sources & Maintien</TabsTrigger>
          <TabsTrigger value="evidence" data-testid="grc-tab-evidence">Chaîne de preuve</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="grc-tab-campaigns">Campagnes</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="grc-tab-incidents">Incidents</TabsTrigger>
          <TabsTrigger value="departments" data-testid="grc-tab-departments">Départements</TabsTrigger>
          <TabsTrigger value="processes" data-testid="grc-tab-processes">Processus</TabsTrigger>
          <TabsTrigger value="taxonomy" data-testid="grc-tab-taxonomy">Taxonomie</TabsTrigger>
          <TabsTrigger value="kris" data-testid="grc-tab-kris">KRIs</TabsTrigger>
          <TabsTrigger value="recos" data-testid="grc-tab-recos">Recommandations</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="grc-tab-heatmap">Heatmap</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="grc-tab-suppliers">Fournisseurs</TabsTrigger>
          <TabsTrigger value="phantoms" data-testid="grc-tab-phantoms">Fantômes</TabsTrigger>
        </TabsList>
        <TabsContent value="analytics"><GovAnalytics /></TabsContent>
        <TabsContent value="intake"><GovIntakeTab onMaterialized={onMaterialized} /></TabsContent>
        <TabsContent value="sources"><GovDocumentsPanel /></TabsContent>
        <TabsContent value="evidence"><EvidenceGraphPanel /></TabsContent>
        <TabsContent value="campaigns"><CampaignsPanel /></TabsContent>
        <TabsContent value="incidents"><IncidentsPanel /></TabsContent>
        <TabsContent value="departments"><DepartmentsTab /></TabsContent>
        <TabsContent value="processes"><ProcessesTab /></TabsContent>
        <TabsContent value="taxonomy"><RiskTaxonomyTab /></TabsContent>
        <TabsContent value="kris"><KRIsTab /></TabsContent>
        <TabsContent value="recos"><RecommendationsTab /></TabsContent>
        <TabsContent value="heatmap"><HeatmapTab /></TabsContent>
        <TabsContent value="suppliers"><SuppliersTab /></TabsContent>
        <TabsContent value="phantoms"><PhantomsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
